import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import * as authApi from '../api/auth';
import { useAuthStore } from '../store/auth';
import '../styles/tokens.css';
import '../styles/base.css';
import '../styles/components.css';

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await authApi.login({ username, password });
      setSession(result.token, result.admin);
      navigate('/dashboard');
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : '登录失败，请检查账号或密码';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '1.1fr 1fr',
      }}
    >
      {/* ——— 左侧装饰面板 ——— */}
      <aside
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(2,6,23,0.88) 0%, rgba(15,23,42,0.68) 42%, rgba(15,23,42,0.22) 100%), url("/login-hero-bg.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          padding: '64px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 48,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: 'inset -120px 0 180px rgba(2,6,23,0.32)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(2,6,23,0.2) 0%, rgba(2,6,23,0.72) 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* 品牌 */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 18,
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'rgba(255,255,255,0.94)',
                display: 'grid',
                placeItems: 'center',
                boxShadow: '0 16px 36px rgba(2,6,23,0.28)',
                overflow: 'hidden',
              }}
            >
              <img
                src="/logo.png"
                alt="Console product logo"
                style={{
                  width: 38,
                  height: 38,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
                Owlsome AI Console
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.62)',
                  marginTop: 2,
                }}
              >
                多租户运营管理平台
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.14)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <span
              style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Service by
            </span>
            <span
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              KZ Datong
            </span>
          </div>
        </div>

        {/* Tagline */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h2
            style={{
              color: 'white',
              fontSize: 34,
              lineHeight: 1.25,
              letterSpacing: 0,
              marginBottom: 12,
              fontWeight: 700,
            }}
          >
            让每条 WhatsApp 询盘<br />自动成为可跟进的商机
          </h2>
          <p
            style={{
              color: 'rgba(255,255,255,0.58)',
              fontSize: 14,
              maxWidth: 440,
              lineHeight: 1.65,
              marginBottom: 28,
            }}
          >
            AI 24/7 响应客户 · 智能提取线索 · 销售漏斗自动化
          </p>

          {/* 能力卡片 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 460 }}>
            {[
              {
                icon: '🤖',
                title: 'AI 客服 7×24 守候',
                desc: 'WhatsApp 原生接入，知识库 RAG 让 AI 真正懂你的产品；客户随时问，关键节点一键人工接管',
              },
              {
                icon: '🎯',
                title: '询盘秒变结构化线索',
                desc: '碎片化对话自动提取产品 / 数量 / 目的港 / 付款方式等字段，LLM 给出完整度与可信度评分，优质客户一眼识别',
              },
              {
                icon: '📈',
                title: '线索 → 商机 → 成单',
                desc: '一键转商机、分配跟进人、阶段流转可视化，完整跟进记录沉淀为团队资产',
              },
            ].map((c) => (
              <div
                key={c.title}
                style={{
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.10)',
                  background: 'rgba(255,255,255,0.04)',
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flex: '0 0 auto',
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 14,
                  }}
                >
                  {c.icon}
                </span>
                <div>
                  <div style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                    {c.title}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12.5, lineHeight: 1.55 }}>
                    {c.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 信任 tags */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginTop: 24,
            }}
          >
            {['WhatsApp 原生', '知识库 RAG', '客户背调评分', '多角色协作'].map((item) => (
              <span
                key={item}
                style={{
                  padding: '5px 10px',
                  borderRadius: 999,
                  color: 'rgba(255,255,255,0.72)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(15,23,42,0.35)',
                  backdropFilter: 'blur(10px)',
                  fontSize: 11.5,
                  fontWeight: 500,
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>

      </aside>

      {/* ——— 右侧表单区 ——— */}
      <main
        style={{
          display: 'grid',
          placeItems: 'center',
          padding: 48,
          background: 'var(--c-bg)',
        }}
      >
        <form
          style={{
            width: '100%',
            maxWidth: 440,
            background: '#fff',
            border: '1px solid var(--c-border)',
            borderRadius: 16,
            padding: '40px 40px 32px',
            boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 12px 32px rgba(15,23,42,0.06)',
          }}
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <h1
            style={{
              fontSize: 22,
              marginBottom: 28,
              letterSpacing: 0,
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            多租户运营管理平台
          </h1>

          {/* 账号字段 */}
          <div className="field">
            <label className="field__label" htmlFor="u">
              账号<span className="req">*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--c-fg-3)',
                  lineHeight: 0,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <input
                id="u"
                className="input"
                type="text"
                placeholder="用户名"
                style={{ paddingLeft: 40 }}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
          </div>

          {/* 密码字段 */}
          <div className="field" style={{ marginTop: 16 }}>
            <label className="field__label" htmlFor="p">
              密码<span className="req">*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--c-fg-3)',
                  lineHeight: 0,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                    ry="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 11V7a5 5 0 0 1 10 0v4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <input
                id="p"
                className="input"
                type="password"
                placeholder="••••••••"
                style={{ paddingLeft: 40 }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="field__hint">3 次失败将锁定 30 分钟</div>
          </div>

          {/* 提交按钮 */}
          <div style={{ marginTop: 24 }}>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={loading}
            >
              {loading ? '登录中…' : '登录控制台'}
            </button>
          </div>

          {/* Helper row */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: 16,
              fontSize: 12.5,
              color: 'var(--c-fg-3)',
            }}
          >
            <a href="#" style={{ fontWeight: 500 }}>
              忘记密码？
            </a>
          </div>
        </form>
      </main>
    </div>
  );
}
